/**
 * useCopilotWindowManager - Copilot 窗口管理 Hook
 * 管理 Copilot 和报告窗口的层级切换、报告窗口的显示/隐藏
 */
import { useCallback, useEffect } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useLocalState, store } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { COPILOT_REPORT_WINDOW_STATE_KEY, COPILOT_ACTIVE_WINDOW_KEY, COPILOT_REPORT_SESSION_ID_KEY } from '../../config/constant';
import { IReportData } from '../../components/ReportViewer/types';
import { useGlobalReportManager } from '../../components/ReportViewer/useGlobalReportManager';

/**
 * 层级设计说明：
 * 
 * 现有层级体系（不能改变）：
 * - .dba-nav-menu-overlay: 2001
 * - .tea-notification-wrap: 2002
 * - .dba-copilot-source-overlay: 2002
 * - .dba-may-ask-filter-item-overlay: 2003
 * - .dba-chart-type-select-overlay: 2003
 * - .dba-chat-share-overlay: 2004
 * - .dba-download-settings-modal: 2005
 * - 历史会话下拉: 2100
 * 
 * Copilot/报告窗口层级（需要低于上述弹出层）：
 * - 基础层级：1990（非激活窗口）
 * - 激活层级：1995（当前聚焦的窗口）
 */
const BASE_Z_INDEX = 1990;
const ACTIVE_Z_INDEX = 1995;

/** 窗口类型 */
export type TWindowType = 'copilot' | 'report';

/** Hook 参数 */
export interface IUseCopilotWindowManagerOptions {
  /** 外部传入的 sessionId，确保与 Copilot 组件同步 */
  sessionId: string;
}

/** Hook 返回类型 */
export interface IUseCopilotWindowManagerReturn {
  /** 报告数据（来自全局报告管理器） */
  dsReport: (IReportData & { show?: boolean; }) | { show: boolean; };
  /** 设置报告数据 */
  setDsReport: (value: { show?: boolean; [key: string]: unknown; }) => void;
  /** 报告窗口是否可见 */
  isReportVisible: boolean;
  /** 报告窗口使用的 sessionId（独立于会话窗口） */
  reportSessionId: string;
  /** 当前活动窗口 */
  activeWindow: TWindowType;
  /** 打开报告窗口 */
  openReportWindow: (reportData: IReportData) => void;
  /** 关闭报告窗口 */
  closeReportWindow: () => void;
  /** 更新报告数据 */
  updateReportData: (update: Partial<IReportData>) => void;
  /** 聚焦 Copilot 窗口 */
  focusCopilot: () => void;
  /** 聚焦报告窗口 */
  focusReport: () => void;
  /** 获取 Copilot 窗口层级 */
  getCopilotZIndex: () => number;
  /** 获取报告窗口层级 */
  getReportZIndex: () => number;
}

export const useCopilotWindowManager = (
  options: IUseCopilotWindowManagerOptions
): IUseCopilotWindowManagerReturn => {
  const { sessionId } = options;
  
  // 同步报告窗口状态到 localStorage，供 AgentRobot 判断
  const [, setReportWindowLocalState] = useLocalState<{ visible: boolean; } | null>(
    COPILOT_REPORT_WINDOW_STATE_KEY,
    null
  );

  // 报告窗口独立的 sessionId，打开报告时保存
  // 这样报告窗口不受会话窗口 sessionId 变化的影响
  const [reportSessionId, setReportSessionId] = useLocalState<string>(
    COPILOT_REPORT_SESSION_ID_KEY,
    ''
  );

  // 报告窗口只在用户明确操作（openReportWindow / handleBackToCopilot）时打开
  const currentSessionId = reportSessionId;
  const { activeReport, updateReport, closeReport } = useGlobalReportManager({ 
    sessionId: currentSessionId 
  });
  
  // dsReport 来自全局管理器
  const dsReport = activeReport || { show: false };

  // 层级管理：使用 useLocalState 实现跨组件同步
  const [activeWindow, setActiveWindow] = useLocalState<TWindowType>(
    COPILOT_ACTIVE_WINDOW_KEY,
    'copilot'
  );

  // 报告窗口是否可见
  // 注意：reportSessionId 有值时，使用 reportSessionId 对应的报告状态
  // 即使切换了 Copilot 会话（sessionId 变化），只要 reportSessionId 不变，报告窗口就应该继续显示
  // aiExcel/magicPoster 场景下 report 数据可能只有 id 没有 document_id，需要同时检查两个字段
  const reportId = (dsReport as any)?.document_id || (dsReport as any)?.id;
  const isReportVisible = !!(dsReport?.show && reportId);

  // 同步报告窗口状态到 AgentRobot
  useEffect(() => {
    setReportWindowLocalState(isReportVisible ? { visible: true } : null);
  }, [isReportVisible]);

  // 打开报告窗口
  const openReportWindow = useMemoizedFn((reportData: IReportData) => {
    if (sessionId) {
      // 重要：先直接写入 store，确保数据先于 reportSessionId 更新
      // 这样下一次渲染时，使用新的 currentSessionId 能读取到数据
      const reportKey = `__dba_report_instances_${sessionId}__`;
      const activeKey = `__dba_report_active_document_id_${sessionId}__`;
      // aiExcel/magicPoster 数据可能只有 id 没有 document_id，需要同时检查两个字段
      const documentId = reportData?.document_id || (reportData as any)?.id;
      
      if (documentId) {
        // 直接写入 store（useLocalState 使用的是 memoryStore，不是 localStorage）
        const existingInstances = store.getItem(reportKey, {}) as Record<string, any>;
        const newInstances = {
          ...existingInstances,
          [documentId]: {
            ...(existingInstances[documentId] || {}),
            ...reportData,
            // 强制补 document_id，防止后续 isReportVisible 判断失败
            document_id: documentId,
            type: reportData.type || 'framework',
            show: true,
          },
        };
        store.setItem(reportKey, newInstances);
        store.setItem(activeKey, documentId);
      }
      
      // 然后更新 reportSessionId，触发重渲染
      // 此时使用新的 currentSessionId 能读取到上面写入的数据
      setReportSessionId(sessionId);
      
      // 同步设置 COPILOT_REPORT_SESSION_ID_KEY，让 Chat 页面能读取到正确的 sessionId
      store.setItem(COPILOT_REPORT_SESSION_ID_KEY, sessionId);
      
      // 打开时立即设置为激活状态（置顶）
      setActiveWindow('report');
    }
  });

  // 关闭报告窗口
  const closeReportWindow = useMemoizedFn(() => {
    // 使用全局报告管理器关闭报告（只设置 show: false，保留数据）
    closeReport();
    // 注意：不清除 reportSessionId，保持报告窗口能使用原有参数调用后端接口
    setActiveWindow('copilot');
  });

  // 更新报告数据
  const updateReportData = useMemoizedFn((update: Partial<IReportData> & { show?: boolean; }) => {
    // 使用全局报告管理器更新报告
    updateReport(update);
    // 如果设置 show: false，切换焦点到 copilot（不清除 reportSessionId）
    if (update.show === false) {
      setActiveWindow('copilot');
    }
  });

  // 兼容 setDsReport 接口（实际调用全局报告管理器的 updateReport）
  const setDsReport = useMemoizedFn((value: { show?: boolean; [key: string]: unknown; }) => {
    if (value.show === false) {
      closeReport();
      // 不清除 reportSessionId，保持报告窗口能使用原有参数
      setActiveWindow('copilot');
    } else {
      updateReport(value as Partial<IReportData>);
    }
  });

  // 聚焦 Copilot 窗口
  const focusCopilot = useMemoizedFn(() => {
    setActiveWindow('copilot');
  });

  // 聚焦报告窗口
  const focusReport = useMemoizedFn(() => {
    setActiveWindow('report');
  });

  // 获取 Copilot 窗口层级
  const getCopilotZIndex = useCallback((): number => activeWindow === 'copilot' ? ACTIVE_Z_INDEX : BASE_Z_INDEX, [activeWindow]);

  // 获取报告窗口层级
  const getReportZIndex = useCallback((): number => activeWindow === 'report' ? ACTIVE_Z_INDEX : BASE_Z_INDEX, [activeWindow]);

  return {
    dsReport,
    setDsReport,
    isReportVisible,
    reportSessionId: currentSessionId,
    activeWindow,
    openReportWindow,
    closeReportWindow,
    updateReportData,
    focusCopilot,
    focusReport,
    getCopilotZIndex,
    getReportZIndex,
  };
};

export default useCopilotWindowManager;
